import React, { useState, useRef, useEffect } from "react";
import "./DateRangePicker.scss";

interface DateRangePickerProps {
  beginningLabelText?: string;
  endingLabelText?: string;
  startDate?: Date | null; 
  endDate?: Date | null; 
  onDateChange?: (range: { startDate: Date | null; endDate: Date | null }) => void; 
  maxDate?: Date | null; 
  className?: string;
}

function formatInputValue(date: Date | null): string {
  if (!date || !(date instanceof Date)) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

function parseTypedDate(value: string): Date | null {
  if (!value.match(/^\d{2}\/\d{2}\/\d{4}$/)) return null;
  const [month, day, year] = value.split("/").map(Number);
  const date = new Date(year, month - 1, day);
  return isNaN(date.getTime()) ? null : date;
}

function isInRange(date: Date, start: Date | null, end: Date | null) {
  if (!start || !end) return false;
  return date > start && date < end;
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];
const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

export const BackArrowIcon = ({ size = 24, color = "#0074CA" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path fillRule="evenodd" clipRule="evenodd"
      d="M14.3938 6.34627L14.4779 6.41889C14.7442 6.68516 14.7684 7.10182 14.5505 7.39543L14.4779 7.47955L10.0086 11.9492L14.4779 16.4189C14.7442 16.6852 14.7684 17.1018 14.5505 17.3954L14.4779 17.4795C14.2116 17.7458 13.795 17.77 13.5014 17.5522L13.4172 17.4795L8.59367 12.6563C8.20315 12.2658 8.20315 11.6326 8.59367 11.2421L13.4172 6.41889C13.6835 6.15262 14.1002 6.12842 14.3938 6.34627Z"
      fill={color}
    />
  </svg>
);

export const ForwardArrowIcon = ({ size = 24, color = "#0074CA" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path fillRule="evenodd" clipRule="evenodd"
      d="M9.60457 17.5522L9.52045 17.4795C9.25418 17.2133 9.22998 16.7966 9.44783 16.503L9.52045 16.4189L13.9898 11.9492L9.52045 7.47955C9.25418 7.21328 9.22998 6.79662 9.44783 6.50301L9.52045 6.41889C9.78672 6.15262 10.2034 6.12842 10.497 6.34627L10.5811 6.41889L15.4047 11.2421C15.7952 11.6326 15.7952 12.2658 15.4047 12.6563L10.5811 17.4795C10.3148 17.7458 9.89818 17.77 9.60457 17.5522Z"
      fill={color}
    />
  </svg>
);

export default function DateRangePicker({
  beginningLabelText = "Start",
  endingLabelText = "End",
  startDate: initialStartDate, 
  endDate: initialEndDate, 
  onDateChange,
  maxDate, 
  className 
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectingStart, setSelectingStart] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [error, setError] = useState(false);
  const [focusStart, setFocusStart] = useState(false);
  const [focusEnd, setFocusEnd] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  

  const [range, setRange] = useState<{ startDate: Date | null; endDate: Date | null }>({
    startDate: initialStartDate || null,
    endDate: initialEndDate || null
  });
  
  const [inputValues, setInputValues] = useState({
    startInput: formatInputValue(initialStartDate ?? null),
    endInput: formatInputValue(initialEndDate ?? null)
  });

  // Sync internal state when parent props change
  useEffect(() => {
    setRange({
      startDate: initialStartDate || null,
      endDate: initialEndDate || null
    });
    setInputValues({
      startInput: formatInputValue(initialStartDate ?? null),
      endInput: formatInputValue(initialEndDate ?? null)
    });
  }, [initialStartDate, initialEndDate]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    setError(
      Boolean((range.startDate && !range.endDate) || (!range.startDate && range.endDate))
    );
  }, [range.startDate, range.endDate]);

function handleInputChange(e: React.ChangeEvent<HTMLInputElement>, isStart: boolean) {
  const value = e.target.value;
  setInputValues(prev => ({
    ...prev,
    [isStart ? "startInput" : "endInput"]: value
  }));

  const parsedDate = parseTypedDate(value);
  const newRange = {
    ...range,
    [isStart ? "startDate" : "endDate"]: parsedDate
  };
  setRange(newRange);

  if (onDateChange) {
    onDateChange(newRange);
  }
};


  function handleInputBlur(isStart: boolean) {
    const inputValue = isStart ? inputValues.startInput : inputValues.endInput;
    const parsedDate = parseTypedDate(inputValue);
    
    if (!parsedDate && inputValue) {

      setInputValues(prev => ({
        ...prev,
        [isStart ? "startInput" : "endInput"]: formatInputValue(isStart ? range.startDate : range.endDate)
      }));
    }
    
    if (isStart) {
      setFocusStart(false);
    } else {
      setFocusEnd(false);
    }
  }

  function renderCalendar(monthOffset = 0) {
    const displayMonth = new Date(currentMonth);
    displayMonth.setMonth(currentMonth.getMonth() + monthOffset);

    const daysInMonth = new Date(displayMonth.getFullYear(), displayMonth.getMonth() + 1, 0).getDate();
    const firstDay = new Date(displayMonth.getFullYear(), displayMonth.getMonth(), 1).getDay();
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div
          key={`empty-${displayMonth.toISOString()}-${i}`}
          className="calendar-day empty"
        />
      );
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(displayMonth.getFullYear(), displayMonth.getMonth(), day);
      date.setHours(0, 0, 0, 0); 
      const isDisabled = maxDate ? date > maxDate : false; 

      const isRangeStart = range.startDate && date.toDateString() === range.startDate.toDateString();
      const isRangeEnd = range.endDate && date.toDateString() === range.endDate.toDateString();
      const inRange = isInRange(date, range.startDate, range.endDate);

      let className = "calendar-day";
      if (isDisabled) className += " disabled";
      if (isRangeStart) className += " range-start";
      if (isRangeEnd) className += " range-end";
      if (inRange) className += " in-range";

      days.push(
        <div
          key={day}
          className={className}
          onClick={() => !isDisabled && handleCalendarDateClick(date)}
        >
          {day}
        </div>
      );
    }

    return (
      <div className="calendar-month">
        <div className="calendar-header">
          {monthOffset === 0 && (
            <button className="nav-button left" onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}>
              <BackArrowIcon />
            </button>
          )}
          <span className="month-title">
            {MONTHS[displayMonth.getMonth()]} {displayMonth.getFullYear()}
          </span>
          {monthOffset === 1 && (
            <button className="nav-button right" onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}>
              <ForwardArrowIcon />
            </button>
          )}
        </div>
        <div className="calendar-weekdays">
          {WEEKDAYS.map((day, index) => (
            <div key={`${day}-${index}`} className="weekday">{day}</div>
          ))}
        </div>
        <div className="calendar-grid">{days}</div>
      </div>
    );
  }

  function handleCalendarDateClick(date: Date) {
    let newRange = { ...range };
    let newInputValues = { ...inputValues };
    
    if (selectingStart) {
      newRange = { startDate: date, endDate: null };
      newInputValues.startInput = formatInputValue(date);
      newInputValues.endInput = "";
    } else {
      newRange = {
        startDate: range.startDate,
        endDate: date > (range.startDate || new Date(0)) ? date : range.startDate
      };
      newInputValues.endInput = formatInputValue(newRange.endDate);
    }
    
    setRange(newRange);
    setInputValues(newInputValues);
    setSelectingStart(!selectingStart);
    
    if (onDateChange) {
      onDateChange(newRange);
    }
  }

  function getLabelClass(value: string, focused: boolean) {
    return value || focused ? "input-label floating" : "input-label";
  }

  return (
    <>
      <div className="date-range-picker-custom" ref={containerRef}>
        <span
          className="calendar-svg"
          tabIndex={0}
          onClick={() => { setIsOpen(true); setSelectingStart(true); }}
          aria-label="Open calendar"
          role="button"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none">
            <path fillRule="evenodd" clipRule="evenodd" d="M17 3L17.1018 3.00685C17.4013 3.04748 17.6446 3.26493 17.7232 3.55062L17.7432 3.64823L17.75 3.75V4.583L18 4.58333C19.6569 4.58333 21 5.92648 21 7.58333V17.5833C21 19.181 19.7511 20.487 18.1763 20.5782L18 20.5833H6C4.34315 20.5833 3 19.2402 3 17.5833V7.58333C3 5.92648 4.34315 4.58333 6 4.58333L6.25 4.583V3.75C6.25 3.3703 6.53215 3.05651 6.89823 3.00685L7 3C7.10355 3 7.20221 3.02099 7.29193 3.05894L7.3892 3.10876L7.44609 3.14703L7.5033 3.19394L7.57361 3.26678L7.63244 3.34669L7.66843 3.40949L7.69593 3.46984L7.71422 3.52045L7.72 3.53929C7.73031 3.57458 7.7381 3.61097 7.74315 3.64823L7.75 3.75V4.583H16.25V3.75L16.2568 3.64823L16.2665 3.59302L16.2858 3.52047L16.3314 3.40978L16.3675 3.34677L16.4393 3.25182L16.4967 3.19394L16.5539 3.147L16.6108 3.10876C16.6961 3.05695 16.7923 3.02168 16.8951 3.00728L17 3ZM19.5 10.083H4.5V17.5833C4.5 18.363 5.09489 19.0038 5.85554 19.0765L6 19.0833H18C18.7797 19.0833 19.4204 18.4884 19.4931 17.7278L19.5 17.5833V10.083ZM10.5 15.3333C11.0523 15.3333 11.5 15.781 11.5 16.3333C11.5 16.8856 11.0523 17.3333 10.5 17.3333C9.94772 17.3333 9.5 16.8856 9.5 16.3333C9.5 15.781 9.94772 15.3333 10.5 15.3333ZM7.5 15.3333C8.05228 15.3333 8.5 15.781 8.5 16.3333C8.5 16.8856 8.05228 17.3333 7.5 17.3333C6.94772 17.3333 6.5 16.8856 6.5 16.3333C6.5 15.781 6.94772 15.3333 7.5 15.3333ZM13.5 15.3333C14.0523 15.3333 14.5 15.781 14.5 16.3333C14.5 16.8856 14.0523 17.3333 13.5 17.3333C12.9477 17.3333 12.5 16.8856 12.5 16.3333C12.5 15.781 12.9477 15.3333 13.5 15.3333ZM13.5 11.8333C14.0523 11.8333 14.5 12.281 14.5 12.8333C14.5 13.3856 14.0523 13.8333 13.5 13.8333C12.9477 13.8333 12.5 13.3856 12.5 12.8333C12.5 12.281 12.9477 11.8333 13.5 11.8333ZM10.5 11.8333C11.0523 11.8333 11.5 12.281 11.5 12.8333C11.5 13.3856 11.0523 13.8333 10.5 13.8333C9.94772 13.8333 9.5 13.3856 9.5 12.8333C9.5 12.281 9.94772 11.8333 10.5 11.8333ZM16.5 11.8333C17.0523 11.8333 17.5 12.281 17.5 12.8333C17.5 13.3856 17.0523 13.8333 16.5 13.8333C15.9477 13.8333 15.5 13.3856 15.5 12.8333C15.5 12.281 15.9477 11.8333 16.5 11.8333ZM16.25 6.083H7.75V6.41667C7.75 6.79636 7.46785 7.11016 7.10177 7.15982L7 7.16667C6.6203 7.16667 6.30651 6.88451 6.25685 6.51844L6.25 6.41667V6.083L6 6.08333C5.2203 6.08333 4.57955 6.67822 4.50687 7.43887L4.5 7.58333V8.583H19.5V7.58333C19.5 6.75491 18.8284 6.08333 18 6.08333L17.75 6.083V6.41667C17.75 6.79636 17.4678 7.11016 17.1018 7.15982L17 7.16667C16.6203 7.16667 16.3065 6.88451 16.2568 6.51844L16.25 6.41667V6.083Z" fill="#626B79"/>
          </svg>
        </span>
        <div className="input-group">
          <div className="input-wrapper">
            <label className={getLabelClass(inputValues.startInput, focusStart)}>
              {beginningLabelText}
            </label>
            <input
              type="text"
              className="date-input"
              value={inputValues.startInput}
              onChange={e => handleInputChange(e, true)}
              onFocus={() => { setIsOpen(true); setSelectingStart(true); setFocusStart(true); }}
              onBlur={() => handleInputBlur(true)}
              onKeyDown={e => {
                if (e.key === "Enter") setIsOpen(false);
              }}
              maxLength={10}
              autoComplete="off"
            />
          </div>
          <span className="divider-svg">
            <svg xmlns="http://www.w3.org/2000/svg" width="2" height="40" viewBox="0 0 2 40" fill="none">
              <path d="M1 0L0.999998 40" stroke="#9FABBB"/>
            </svg>
          </span>
          <div className="input-wrapper">
            <label className={getLabelClass(inputValues.endInput, focusEnd)}>
              {endingLabelText}
            </label>
            <input
              type="text"
              className="date-input"
              value={inputValues.endInput}
              onChange={e => handleInputChange(e, false)}
              onFocus={() => { setIsOpen(true); setSelectingStart(false); setFocusEnd(true); }}
              onBlur={() => handleInputBlur(false)}
              onKeyDown={e => {
                if (e.key === "Enter") setIsOpen(false);
              }}
              maxLength={10}
              autoComplete="off"
            />
          </div>
        </div>
        {isOpen && (
          <div className="calendar-dropdown">
            <div className="calendar-container">
              {renderCalendar(0)}
              {renderCalendar(1)}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
